import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './DocumentPicker.sass'
import {
  DocumentPickerViewModel,
  DocumentPickerViewModelItem,
} from './DocumentPickerViewModel'
import {
  ModalSheet,
  ModalSheetSearchHeader,
  ModalSheetContent,
} from 'components/ModalSheet'
import { Id } from '../../../types/definitions'
import { useCombobox } from 'downshift'
import { IconType } from '../../../utils/icon-util'
import FileTypeIcon from 'components/FileTypeIcon'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'
import { useInfiniteScroll } from '../../../hooks/infinite-scroll'

interface DocumentPickerProps {
  vm: DocumentPickerViewModel
  title?: React.ReactNode
  searchPlaceholder?: string
  ['data-test']?: string
}

// Suppress ref errors in Downshift.
// We don't always render the underlying component,
// so Downshift complains. But we got this.
const SUPPRESS = { suppressRefError: true }

export default observer(function DocumentPicker({
  vm,
  'data-test': dataTest,
  searchPlaceholder = 'Type to search. You can use arrow keys and Enter here.',
}: DocumentPickerProps) {
  const scrollMonitorRef = React.useRef<HTMLDivElement | null>(null)
  useInfiniteScroll(scrollMonitorRef, vm.scrollProvider)
  const { getItemProps, getMenuProps, getInputProps, highlightedIndex } =
    useCombobox({
      isOpen: vm.showing,
      items: vm.documents,
      selectedItem: null,
      onSelectedItemChange: (c) => c.selectedItem?.select(),
      isItemDisabled: (item) => item.select.pending,
    }) as any // Missing TS defs for the suppress opts apparently. RIP.

  const inputProps = getInputProps(undefined, SUPPRESS)

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      {...getInputProps(undefined, SUPPRESS)}
    >
      <ModalSheetSearchHeader
        bordered
        withCloseButton
        focus$={vm.showing$}
        placeholder={searchPlaceholder}
        data-test={`${dataTest}-search-input`}
        onChange={vm.setSearchText}
        value={vm.searchText}
        onKeyDown={inputProps.onKeyDown}
        lonelyChild={
          // These conditions will be true when we don't render anything else.
          vm.documents.length === 0 &&
          vm.scrollProvider.isDone &&
          Boolean(vm.searchText)
        }
        ref={inputProps.ref}
      />
      <ModalSheetContent {...getMenuProps(undefined, SUPPRESS)}>
        {vm.documents.map((docVm, i) => {
          const itemProps = getItemProps(
            {
              index: i,
              key: Id.assert(docVm.document.id),
              item: docVm,
            },
            SUPPRESS,
          )
          return (
            <DocumentItem
              key={Id.assert(docVm.document.id)}
              vm={docVm}
              ref={itemProps.ref}
              data-test={`${dataTest}-document-${docVm.document.id}`}
              onMouseMove={itemProps.onMouseMove}
              onClick={itemProps.onClick}
              highlighted={i === highlightedIndex}
            />
          )
        })}
        {vm.showing && !vm.scrollProvider.isDone && (
          <div
            className={cx(
              styles.loading,
              vm.documents.length === 0 && styles.empty,
            )}
            ref={scrollMonitorRef}
          >
            <Spinner />
          </div>
        )}
      </ModalSheetContent>
    </ModalSheet>
  )
})

interface DocumentItemProps {
  vm: DocumentPickerViewModelItem
  highlighted: boolean
  onClick?: () => unknown
  onMouseMove?: React.MouseEventHandler
  ['data-test']?: string
}

const DocumentItem = observer(
  React.forwardRef(function DocumentItem(
    {
      vm,
      highlighted,
      onClick,
      onMouseMove,
      'data-test': dataTest,
    }: DocumentItemProps,
    ref: React.Ref<HTMLDivElement>,
  ) {
    const document = vm.document
    const isSelected = vm.isSelected
    const icon = IconType(vm.fileName)
    const fileName = vm.fileName
    return (
      <div
        role="option"
        aria-selected={isSelected}
        className={cx(
          styles.item,
          isSelected && styles.selected,
          highlighted && styles.highlighted,
        )}
        data-test={dataTest}
        ref={ref}
        onClick={onClick}
        onMouseMove={onMouseMove}
      >
        <div className={styles.icon}>
          <FileTypeIcon
            height={36}
            primaryColor={icon.color}
            strokeColor="#bcbcbe"
            type={icon.type}
          />
        </div>
        <div className={styles.info}>
          <div className={styles.title} data-test={`${dataTest}-title`}>
            {document.title}
          </div>
          <div className={styles.subtitle}>{fileName}</div>
        </div>
        <div className={styles.extras}>
          {vm.select.pending ? (
            <Spinner />
          ) : isSelected ? (
            <div className={styles.icon}>
              <Icon name="radio-checked" />
            </div>
          ) : null}
        </div>
      </div>
    )
  }),
)
